import type { RefObject } from 'react';
import { useCallback } from 'react';

import { FOLLOW_BOTTOM_THRESHOLD_PX } from './chatScroll.constants';

interface UseDockShrinkNudgeArgs {
  scrollRef: RefObject<HTMLDivElement>;
  snapPendingRef: RefObject<boolean>;
  scrollProgrammatically: (run: () => void) => void;
}

export const useDockShrinkNudge = ({
  scrollRef,
  snapPendingRef,
  scrollProgrammatically,
}: UseDockShrinkNudgeArgs) => {
  // The composer dock is a sibling below the scroller. When it grows —
  // most often the async follow-up suggestions / action bar landing after
  // a turn settles, or an approval card mounting — it eats height from the
  // bottom of the scroller, hiding whatever output sat at the bottom edge
  // behind the dock. Nudge the scroll DOWN by exactly the shrink amount so
  // that bottom-edge content stays visible above the dock (the browser
  // clamps when there's nothing below). Down-only (we only ever add to
  // scrollTop) so it can't yank a reader who scrolled up, and skipped
  // mid-send-snap so it can't fight the one-shot send snap.
  const onScrollerResize = useCallback(
    (shrankBy: number) => {
      const scroller = scrollRef.current;
      if (!scroller) return;
      if (shrankBy <= 0 || snapPendingRef.current) return;
      const distanceAfterShrink =
        scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      const distanceBeforeShrink = distanceAfterShrink - shrankBy;
      if (distanceBeforeShrink > FOLLOW_BOTTOM_THRESHOLD_PX) return;
      scrollProgrammatically(() => {
        scroller.scrollTo({ top: scroller.scrollTop + shrankBy });
      });
    },
    [scrollRef, snapPendingRef, scrollProgrammatically]
  );

  return { onScrollerResize };
};
